import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  UserCircleIcon,
  ScanIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  TrashIcon,
  RefreshCwIcon,
  ShieldCheckIcon,
  InfoIcon
} from 'lucide-react';
import { toast } from 'sonner';

const UltraAdminProfile = () => {
  const [faceEnrolled, setFaceEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [facialData, setFacialData] = useState(null);

  useEffect(() => {
    // Check if face is already enrolled
    checkFaceEnrollment();
  }, []);

  const checkFaceEnrollment = async () => {
    // TODO: API call to check if ultra admin has facial enrollment
    // For now, check localStorage
    const enrolled = localStorage.getItem('ultra_admin_face_enrolled');
    if (enrolled) {
      setFaceEnrolled(true);
      setFacialData(JSON.parse(enrolled));
    }
  };

  const handleEnrollFace = async () => {
    setEnrolling(true);
    try {
      toast.info('Yüz tanıma özelliği yakında aktif olacak!');
      // TODO: FaceIO integration
      // 1. Initialize FaceIO
      // 2. Call enroll()
      // 3. Save facial_id to backend
      // 4. Update state
      
      setTimeout(() => {
        setEnrolling(false);
      }, 2000);
    } catch (error) {
      console.error('Face enrollment error:', error);
      toast.error('Yüz kaydı başarısız oldu');
      setEnrolling(false);
    }
  };

  const handleDeleteFace = async () => {
    if (window.confirm('Kayıtlı yüzünüzü silmek istediğinizden emin misiniz?')) {
      try {
        // TODO: API call to delete facial enrollment
        localStorage.removeItem('ultra_admin_face_enrolled');
        setFaceEnrolled(false);
        setFacialData(null);
        toast.success('Yüz kaydı silindi');
      } catch (error) {
        console.error('Face deletion error:', error);
        toast.error('Yüz kaydı silinirken hata oluştu');
      }
    }
  };

  const ultraAdmin = JSON.parse(localStorage.getItem('ultra_admin_user') || '{}');

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card className="p-6">
        <div className="flex items-start space-x-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <UserCircleIcon className="w-16 h-16 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Ultra Admin Profil</h2>
            <p className="text-gray-600 mt-1">{ultraAdmin.email || 'ultra@advancecash.io'}</p>
            <p className="text-sm text-gray-500 mt-2">{ultraAdmin.full_name || 'Ultra Administrator'}</p>
            <div className="mt-3">
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                Süper Yönetici
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* Face Recognition Settings */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
            <ScanIcon className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Yüz Tanıma Güvenliği</h3>
            <p className="text-sm text-gray-500">Kullanıcı hesaplarına geçiş için yüz tanıma</p>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <InfoIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">Yüz Tanıma Nedir?</p>
              <p className="text-xs text-blue-700 mt-1">
                Kullanıcı hesaplarına geçiş yapmak için yüzünüzü kaydedin. Bu sayede sadece siz kullanıcı impersonation özelliğini kullanabilirsiniz.
              </p>
            </div>
          </div>
        </div>

        {/* Enrollment Status */}
        <div className="space-y-4">
          {faceEnrolled ? (
            <>
              {/* Enrolled State */}
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Yüz Tanıma Aktif</p>
                    <p className="text-sm text-green-700">
                      Yüzünüz başarıyla kaydedildi
                    </p>
                  </div>
                </div>
                <ShieldCheckIcon className="w-8 h-8 text-green-600" />
              </div>

              {facialData && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Kayıt Tarihi</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(facialData.enrolled_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Facial ID</p>
                    <p className="text-sm font-medium text-gray-900 font-mono">
                      {facialData.facial_id?.substring(0, 12)}...
                    </p>
                  </div>
                </div>
              )}

              {/* Actions for enrolled state */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleEnrollFace}
                  disabled={enrolling}
                  className="flex-1"
                >
                  <RefreshCwIcon className="w-4 h-4 mr-2" />
                  Yeniden Kaydet
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteFace}
                  className="flex-1 text-red-600 hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Kaydı Sil
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Not Enrolled State */}
              <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <AlertCircleIcon className="w-6 h-6 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-900">Yüz Tanıma Kaydı Yok</p>
                    <p className="text-sm text-yellow-700">
                      Kullanıcı geçişi için yüzünüzü kaydedin
                    </p>
                  </div>
                </div>
              </div>

              {/* Enroll Button */}
              <Button
                onClick={handleEnrollFace}
                disabled={enrolling}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12"
              >
                {enrolling ? (
                  <>
                    <RefreshCwIcon className="w-5 h-5 mr-2 animate-spin" />
                    Yüz Taranıyor...
                  </>
                ) : (
                  <>
                    <ScanIcon className="w-5 h-5 mr-2" />
                    Yüzümü Kaydet
                  </>
                )}
              </Button>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Nasıl Çalışır?</h4>
          <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
            <li>Yukarıdaki butona tıklayın</li>
            <li>Kamera izni verin</li>
            <li>Yüzünüzü kameraya gösterin</li>
            <li>Sistem yüzünüzü kaydedecek</li>
            <li>Artık kullanıcı geçişi yapabilirsiniz</li>
          </ol>
        </div>
      </Card>

      {/* Security Notice */}
      <Card className="p-6 bg-purple-50 border-purple-200">
        <div className="flex items-start space-x-3">
          <ShieldCheckIcon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-medium text-purple-900">Güvenlik ve Gizlilik</h4>
            <p className="text-sm text-purple-700 mt-1">
              Yüz tanıma verileriniz şifreli olarak saklanır ve sadece kimlik doğrulama için kullanılır. 
              Hiçbir yüz görüntüsü veya video kaydedilmez, sadece matematiksel bir temsil (biometric hash) oluşturulur.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UltraAdminProfile;
